/**
 * QuotationDisplay Components
 * 
 * Centralized display components for consistent quotation rendering
 * across all package types (DMC, Custom, Manual) and views (Preview, Detail, PDF).
 * 
 * All components:
 * - Accept normalized data from QuotationFormatter
 * - Support variant prop: 'preview' | 'detail' | 'pdf'
 * - Include data-package-type attribute for debugging
 * 
 * Usage:
 *   import { 
 *     HotelsByCategoryTable,
 *     DMCPricingTable,
 *     MealPlanPricingTable,
 *     TourRouteChips,
 *     SketchItineraryTable,
 *     DetailedItinerary,
 *     InclusionsExclusions,
 *     MiscCostsTable,
 *     VehicleInfo,
 *     P2PTransfersTable,
 *     GrandTotalSummary
 *   } from '@/components/QuotationDisplay';
 *   
 *   // With QuotationFormatter
 *   const formatter = new QuotationFormatter(quotation, agency);
 *   
 *   <HotelsByCategoryTable 
 *     data={formatter.getHotelsByCategory()} 
 *     packageType={quotation.route_type}
 *     variant="detail"
 *   />
 */

// Universal Display Components
export { default as HotelsByCategoryTable } from './HotelsByCategoryTable';
export { default as DMCPricingTable } from './DMCPricingTable';
export { default as MealPlanPricingTable } from './MealPlanPricingTable';
export { default as PricingMobileCards } from './PricingMobileCards';
export { default as TourRouteChips } from './TourRouteChips';
export { default as SketchItineraryTable } from './SketchItineraryTable';
export { default as DetailedItinerary } from './DetailedItinerary';
export { default as InclusionsExclusions } from './InclusionsExclusions';
export { default as MiscCostsTable } from './MiscCostsTable';
export { default as VehicleInfo } from './VehicleInfo';
export { default as P2PTransfersTable } from './P2PTransfersTable';
export { default as GrandTotalSummary } from './GrandTotalSummary';

// Configuration & Utilities
export {
  // Column configuration
  PRICING_COLUMN_ORDER,
  PRICING_COLUMN_LABELS,
  getVisiblePricingColumns,
  
  // Grand total rules
  shouldHideGrandTotal,
  
  // Hotel table config
  HOTEL_TABLE_COLUMNS,
  MEAL_PLAN_COLORS,
  getMealPlanColor,
  
  // Formatting utilities
  formatCurrency,
  formatDate,
  formatDateRange,
  
  // Calculations
  calculatePaxBreakdown,
  calculateHotelDates,
  
  // Styling variants
  COMPONENT_VARIANTS,
  formatItineraryTitle
} from './displayConfig';

// Default export with all components
import HotelsByCategoryTable from './HotelsByCategoryTable';
import DMCPricingTable from './DMCPricingTable';
import MealPlanPricingTable from './MealPlanPricingTable';
import PricingMobileCards from './PricingMobileCards';
import TourRouteChips from './TourRouteChips';
import SketchItineraryTable from './SketchItineraryTable';
import DetailedItinerary from './DetailedItinerary';
import InclusionsExclusions from './InclusionsExclusions';
import MiscCostsTable from './MiscCostsTable';
import VehicleInfo from './VehicleInfo';
import P2PTransfersTable from './P2PTransfersTable';
import GrandTotalSummary from './GrandTotalSummary';
import * as displayConfig from './displayConfig';

export default {
  // Components
  HotelsByCategoryTable,
  DMCPricingTable,
  MealPlanPricingTable,
  PricingMobileCards,
  TourRouteChips,
  SketchItineraryTable,
  DetailedItinerary,
  InclusionsExclusions,
  MiscCostsTable,
  VehicleInfo,
  P2PTransfersTable,
  GrandTotalSummary,
  // Config
  ...displayConfig
};
